'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import BeforeAfter from './BeforeAfter'
import type { Media as MediaType } from '@/payload-types'

// Match the BeforeAfterImage interface from BeforeAfter component
export interface BeforeAfterImage {
  resource?: MediaType | null // Payload media object (preferred)
  src?: string // Fallback for processed data
  alt: string
  label?: string // Optional label for modal display
  focalX?: number | null // Keep for backward compatibility
  focalY?: number | null
}

export interface BeforeAfterModalProps {
  isOpen: boolean
  onClose: () => void
  left: BeforeAfterImage
  right: BeforeAfterImage
  leftLabel?: string
  rightLabel?: string
}

export default function BeforeAfterModal({
  isOpen,
  onClose,
  left,
  right,
  leftLabel = 'VORHER',
  rightLabel = 'NACHHER',
}: BeforeAfterModalProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[99999] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal Content */}
          <motion.div
            className="relative w-full max-w-4xl mx-4 bg-background overflow-hidden shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 inline-flex h-12 w-12 items-center justify-center bg-brand text-white shadow-lg transition-opacity hover:opacity-90"
              aria-label="Schließen"
            >
              <X size={24} />
            </button>

            {/* Before/After Slider */}
            <BeforeAfter
              left={left}
              right={right}
              leftLabel={leftLabel}
              rightLabel={rightLabel}
              heightClass="h-[60vh] md:h-[70vh]"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
